import type { AppThunk } from "@Store/index";
import { getMetaSuccess } from "@App/appReducer";
import {
  REGION_PAGE_SIZE,
  getListingKey,
  getRegionScope,
  getRegionTab,
} from "@Pages/Blog/BlogHomeRevamp/settings";
import { loadFirstListingPage } from "@Pages/Blog/BlogAllPosts/data/fetchBlogListingPage";
import { fetchRegionDescription } from "@Pages/Blog/BlogHomeRevamp/utils/blogHomeCms";
import {
  fetchPublishedBlogCountry,
  isBlogCountryRegionLive,
} from "@Pages/Blog/BlogHomeRevamp/utils/fetchLiveBlogRegions";
import { resolveBlogRegionByPageSlug } from "@Pages/Blog/BlogHomeRevamp/utils/blogRegionModel";
import {
  setRevampRegionPageDeniedSlug,
  setRevampRegionPageReadySlug,
  updateRevampRegionDescription,
} from "@Pages/Blog/BlogHome/data/blogHomeReducer";

const getBlogRegionData =
  ({
    slug,
    stage,
    isEnabled,
    staticContext,
    skipRegionLiveGate = false,
  }: {
    slug?: string;
    stage?: string | null;
    isEnabled: boolean;
    staticContext?: any;
    /** Region URL already passed CMS live gate this session (tab/stage refetch only). */
    skipRegionLiveGate?: boolean;
  }): AppThunk =>
  async (dispatch, getState) => {
    const pageSlug = slug?.trim();
    if (!isEnabled || !pageSlug) {
      dispatch(setRevampRegionPageDeniedSlug(pageSlug ?? null));
      if (staticContext) staticContext.statusCode = 404;
      return;
    }

    let region = resolveBlogRegionByPageSlug(pageSlug, null);
    if (!region) {
      const cmsCountry = await fetchPublishedBlogCountry(pageSlug);
      region = resolveBlogRegionByPageSlug(pageSlug, cmsCountry);
    }

    if (!region) {
      dispatch(setRevampRegionPageDeniedSlug(pageSlug));
      if (staticContext) staticContext.statusCode = 404;
      return;
    }

    const { revampRegionPageDeniedSlug, revampRegionPageReadySlug } = getState().blogHomeData;

    if (revampRegionPageDeniedSlug === pageSlug) {
      if (staticContext) staticContext.statusCode = 404;
      return;
    }

    const needsLiveGate = !skipRegionLiveGate || revampRegionPageReadySlug !== pageSlug;

    if (needsLiveGate) {
      if (revampRegionPageDeniedSlug && revampRegionPageDeniedSlug !== pageSlug) {
        dispatch(setRevampRegionPageDeniedSlug(null));
      }

      const isLive = await isBlogCountryRegionLive(region.countrySlug);
      if (!isLive) {
        dispatch(setRevampRegionPageDeniedSlug(pageSlug));
        if (staticContext) staticContext.statusCode = 404;
        return;
      }

      dispatch(setRevampRegionPageDeniedSlug(null));
    }

    const tab = getRegionTab(stage);
    const scope = getRegionScope(region.countrySlug);

    const meta: any = {
      title: `${region.name} Student Guides and Articles | Amber`,
      description: `Student guides for ${region.name}: neighbourhoods, renting, budgets, moving in and universities.`,
      keywords: [`${region.name} student accommodation`, "student housing guides", "student blog"],
      canonical_url_suffix: `/blog/region/${region.slug}`,
    };
    dispatch(getMetaSuccess(meta));

    const descriptionRequest = getState().blogHomeData.revampRegionDescriptions[region.countrySlug]
      ? Promise.resolve(null)
      : fetchRegionDescription(region.countrySlug);

    const isLoaded = await dispatch(
      loadFirstListingPage(scope, {
        key: getListingKey(scope, tab.id),
        pageSize: REGION_PAGE_SIZE,
        categorySlug: tab.categorySlug,
        stageSlug: tab.stageSlug,
        countrySlug: region.countrySlug,
      }),
    );
    const description = await descriptionRequest;
    if (description) {
      dispatch(updateRevampRegionDescription({ countrySlug: region.countrySlug, description }));
    }

    if (staticContext && isLoaded === false) {
      staticContext.statusCode = 500;
      return;
    }

    dispatch(setRevampRegionPageReadySlug({ pageSlug, region }));
  };

export default getBlogRegionData;
